import { Component } from '@verndale/core';
import { gsap, Expo } from 'gsap';
import Axios from 'axios';
import { keyCode, debounce } from '../../helpers';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      html: document.querySelector('html'),
      body: document.querySelector('body'),
      pageContainer: document.querySelector('.page-container'),
      stickyMenu: this.el.querySelector('.header__sticky'),
      form: this.el.querySelector('.header-search__bar form'),
      trigger: this.el.querySelector('.header__search-toggle'),
      triggerIconOpen: this.el.querySelector('.header__search-toggle-icon-open'),
      triggerIconClose: this.el.querySelector('.header__search-toggle-icon-close'),
      drawer: this.el.querySelector('.header-search'),
      input: this.el.querySelector('.header-search__input'),
      clear: this.el.querySelector('.header-search__clear'),
      submit: this.el.querySelector('.header-search__submit'),
      suggestions: this.el.querySelector('.header-search__suggestions'),
      initialSuggestions: this.el.querySelector(
        '.header-search__suggestions-initial'
      ),
      loadedSuggestions: this.el.querySelector(
        '.header-search__suggestions-loaded'
      ),
      loadedSuggestionsList: this.el.querySelector(
        '.header-search__suggestions-loaded .header-search__suggestion-list'
      )
    };

    this.endpoint = this.dom.drawer.dataset.suggestionsEndpoint;
    this.language = this.dom.drawer.dataset.language;
    this.action = this.dom.form.getAttribute('action');
    this.initialSuggestions = !!this.dom.initialSuggestions;
  }

  addEventListeners() {
    this.el.addEventListener('keydown', this.handleEscape.bind(this));
    this.dom.input.addEventListener('focus', this.handleTextInputFocus.bind(this));
    this.dom.input.addEventListener('blur', this.handleTextInputBlur.bind(this));
    this.dom.input.addEventListener('change', this.checkInputValue.bind(this));
    this.dom.input.addEventListener(
      'keydown',
      this.navigateSuggestions.bind(this)
    );
    this.dom.input.addEventListener(
      'keyup',
      debounce(this.handleLoadSuggestions.bind(this), 300)
    );
    this.dom.clear.addEventListener('click', this.handleClearSearch.bind(this));
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  removeEventListeners() {
    this.el.removeEventListener('keydown', this.handleEscape.bind(this));
    this.dom.input.removeEventListener('focus', this.handleTextInputFocus.bind(this));
    this.dom.input.removeEventListener('blur', this.handleTextInputBlur.bind(this));
    this.dom.input.removeEventListener('change', this.checkInputValue.bind(this));
    this.dom.input.removeEventListener(
      'keydown',
      this.navigateSuggestions.bind(this)
    );
    this.dom.input.removeEventListener(
      'keyup',
      debounce(this.handleLoadSuggestions.bind(this), 300)
    );
    this.dom.clear.removeEventListener('click', this.handleClearSearch.bind(this));
  }

  handleTextInputFocus(e) {
    e.target.parentNode.classList.add('is-focused');
  }

  handleTextInputBlur(e) {
    e.target.parentNode.classList.remove('is-focused');
  }

  checkInputValue(e) {
    if (e.target.value) {
      e.target.parentNode.classList.add('is-filled');
    } else {
      e.target.parentNode.classList.remove('is-filled');
    }
  }

  setInitialSuggestions() {
    if (this.dom.initialSuggestions) {
      this.initialSuggestions = true;
      this.dom.submit.classList.remove('header-search__submit-btn');
      this.dom.initialSuggestions.style.display = 'block';
      this.dom.loadedSuggestions.style.display = 'none';
    } else {
      this.suggestionsOpen = false;
      gsap.to(this.dom.suggestions, {
        height: 0,
        duration: 0.5,
        ease: Expo.easeInOut
      });
    }
  }

  toggleDrawer() {
    if (this.el.classList.contains('header--search-open')) {
      this.closeDrawer();
      document.dispatchEvent(new Event('menuClose'));
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    const tl = gsap.timeline();
    const { innerHeight, scrollY } = window;
    const { offsetTop } = this.dom.pageContainer;
    const headerHeight = this.dom.stickyMenu.offsetHeight;
    const difference = offsetTop - scrollY;
    const isTabletLandscape = window.matchMedia('(min-width: 1024px)');

    if (!isTabletLandscape.matches) this.dom.html.classList.add('is-scroll-locked');

    gsap.to(this.dom.triggerIconOpen, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.to(this.dom.triggerIconOpen, { display: 'none' });
        gsap.to(this.dom.triggerIconClose, { display: 'flex' });
        gsap.to(this.dom.triggerIconClose, {
          opacity: 1,
          duration: 0.3
        })
      }
    });

    if (this.dom.initialSuggestions) {
      this.suggestionsOpen = true;
      tl.to(this.dom.suggestions, {
        height: 'auto',
        duration: 0.5,
        ease: Expo.easeInOut
      });
    }

    this.el.classList.add('header--search-open');
    tl.set(this.dom.drawer, {
      height: 0,
      autoAlpha: 1,
      onComplete: () => {
        this.dom.input.focus({ preventScroll: true });
      }
    });
    tl.to(this.dom.drawer, {
      height: (isTabletLandscape.matches ? 'auto' : `${innerHeight - headerHeight - (difference > 0 ? difference : 0)}px`),
      minHeight: (isTabletLandscape.matches ? '400px' : ''),
      maxHeight: (isTabletLandscape.matches ? `${innerHeight - headerHeight}px` : ''),
      duration: 0.4,
      onComplete: () => {
        this.dom.drawer.classList.add('overflow-auto');
      }
    });

    this.addEventListeners();
  }

  closeDrawer() {
    const tl = gsap.timeline();
    this.el.classList.remove('header--search-open');
    gsap.to(this.dom.triggerIconClose, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.to(this.dom.triggerIconClose, { display: 'none' });
        gsap.to(this.dom.triggerIconOpen, { display: 'flex' });
        gsap.to(this.dom.triggerIconOpen, {
          opacity: 1,
          duration: 0.3
        })
      }
    });

    tl.to(this.dom.drawer, {
      height: 0,
      padding: 0,
      minHeight: 0,
      maxHeight: 0,
      duration: 0.4,
      onComplete: () => {
        this.dom.loadedSuggestions.style.display = 'none';
        this.dom.drawer.classList.remove('overflow-auto');

        // Unselect any selected option
        this.unselectSuggestions();

        if (this.dom.initialSuggestions) {
          this.initialSuggestions = true;
          this.dom.initialSuggestions.style.display = 'block';
        }

        // Clear input
        this.dom.input.value = '';
        this.dom.clear.style.display = 'none';
        this.dom.submit.style.display = 'none';
        this.dom.trigger?.focus({ preventScroll: true });
      }
    });
    tl.set(this.dom.drawer, { height: null, autoAlpha: 0, padding: '' });

    this.removeEventListeners();

    if (this.suggestionsOpen) {
      tl.to(this.dom.suggestions, {
        height: 0,
        duration: 0.2
      });
    }
  }

  handleClickOutside(e) {
    if (!this.dom.drawer.contains(e.target) && (!this.dom.trigger.contains(e.target))){
      this.closeDrawer();
    }
  }

  handleClearSearch() {
    this.dom.input.value = '';
    this.dom.clear.style.display = 'none';
    this.dom.submit.style.display = 'none';
    this.setInitialSuggestions();
    this.unselectSuggestions();
    this.dom.input.focus({ preventScroll: true });
  }

  async handleLoadSuggestions(e) {
    const key = e.which || e.keyCode;
    const { value } = e.target;

    if (
      [
        keyCode.UP,
        keyCode.DOWN,
        keyCode.HOME,
        keyCode.END,
        keyCode.ESC,
        keyCode.TAB
      ].includes(key)
    ) {
      return;
    }

    this.unselectSuggestions();

    if (value) {
      this.dom.clear.style.display = 'block';
      this.dom.submit.style.display = 'block';
    } else {
      this.dom.clear.style.display = 'none';
      this.dom.submit.style.display = 'none';
    }

    if (value.length > 1) {
      const { data } = await Axios({
        url: this.endpoint,
        params: { q: value, language: this.language }
      });

      if (data.suggestions?.length) {
        this.initialSuggestions = false;

        this.dom.loadedSuggestionsList.innerHTML = data.suggestions
          .map(
            suggestion =>
              `<a href="${this.action}?query=${suggestion}" tabindex="-1"><li class="header-search__suggestions-list-item">${suggestion}</li></a>`
          )
          .join('');
        this.dom.submit.classList.add('header-search__submit-btn');
        this.dom.loadedSuggestions.style.display = 'block';

        if (this.dom.initialSuggestions) {
          this.dom.initialSuggestions.style.display = 'none';
        } else {
          this.suggestionsOpen = true;
          gsap.to(this.dom.suggestions, {
            height: 'auto',
            duration: 0.5,
            ease: Expo.easeInOut
          });
        }
      } else {
        this.setInitialSuggestions();
      }
    } else {
      this.setInitialSuggestions();
    }
  }

  handleEscape(e) {
    const key = e.which || e.keyCode;

    if (key === keyCode.ESC) {
      e.stopPropagation();
      this.closeDrawer();
    }
  }

  navigateSuggestions(e) {
    const key = e.which || e.keyCode;

    const suggestions = this.initialSuggestions
      ? [...this.dom.initialSuggestions.querySelectorAll('a')]
      : [...this.dom.loadedSuggestions.querySelectorAll('a')];
    const selectedSuggestion = this.initialSuggestions
      ? this.dom.initialSuggestions.querySelector('a.selected')
      : this.dom.loadedSuggestions.querySelector('a.selected');

    if (!suggestions.length) {
      return;
    }

    switch (key) {
      case keyCode.UP:
        e.preventDefault();
        this.unselectSuggestions();

        const previousOption =
          selectedSuggestion?.parentNode.previousElementSibling?.querySelector(
            'a'
          ) || suggestions[suggestions.length - 1];

        this.selectSuggestion(previousOption);

        break;
      case keyCode.DOWN:
        e.preventDefault();
        this.unselectSuggestions();

        const nextOption =
          selectedSuggestion?.parentNode.nextElementSibling?.querySelector(
            'a'
          ) || suggestions[0];

        this.selectSuggestion(nextOption);

        break;
      case keyCode.HOME:
        e.preventDefault();
        this.unselectSuggestions();
        this.selectSuggestion(suggestions[0]);
        break;
      case keyCode.END:
        e.preventDefault();
        this.unselectSuggestions();
        this.selectSuggestion(suggestions[suggestions.length - 1]);
        break;
      case keyCode.TAB:
        this.unselectSuggestions();
        break;
    }
  }

  selectSuggestion(suggestion) {
    suggestion.classList.add('selected');
    this.dom.input.value = suggestion.textContent;
    this.dom.clear.style.display = 'block';
    this.dom.submit.style.display = 'block';
  }

  unselectSuggestions() {
    const selectedSuggestion =
      this.dom.initialSuggestions?.querySelector('a.selected') ||
      this.dom.loadedSuggestions.querySelector('a.selected');
    selectedSuggestion?.classList.remove('selected');
  }
}

export default Module;
